import SportIcon from '@/components/SportIcon';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Users, Grid3x3, List, Trophy, Crown, Star,
  IndianRupee, RefreshCw, Plus, Trash2, Edit2, Save, X, Upload, Camera, ChevronDown, ChevronUp, ArrowLeftRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

// Sport icons (emoji fallback when no real icon)
const SPORT_ICONS = {
  table_tennis: '🏓', badminton: '🏸', chess: '♟️',
  carrom: '🎯', pickleball: '🥒', squash: '🎾',
  cricket: '🏏', football: '⚽', default: '🏆',
};

const CATEGORY_CONFIG = {
  champion: { label: 'Champions', icon: '👑', color: '#f5c518' },
  advanced: { label: 'Advanced',  icon: '⭐', color: '#f5c518' },
};

export default function TeamDetailPage() {
  const { teamId }   = useParams();
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin   = isAdmin();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token   = () => localStorage.getItem('btccp_access_token');

  const [team, setTeam]           = useState(null);
  const [roster, setRoster]       = useState([]);
  const [sports, setSports]           = useState([]);
  const [sportFormats, setSportFormats] = useState({}); // sport_code → {platinum_count}
  const [categories, setCategories] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTid, setSelectedTid] = useState('');
  const [auctionPicks, setAuctionPicks] = useState({});
  const [teamAssignments, setTeamAssignments] = useState({});  // all players already in a team
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('sport');
  // Selection state for bulk remove
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRemoving, setBulkRemoving] = useState(false);
  // Team matches state
  const [teamEncounters, setTeamEncounters] = useState([]);
  const [encLoading, setEncLoading] = useState(false);
  const [openEncId, setOpenEncId] = useState(null); // expanded encounter
  const [koAchievements, setKoAchievements] = useState({}); // sport_code → 'winner'|'runner_up'|'semi'
  // Edit team state
  const [editOpen, setEditOpen]   = useState(false);
  const [editForm, setEditForm]   = useState({ name: '', short_name: '', color: '#3b82f6' });
  const [editLogoB64, setEditLogoB64] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  const [editPhotoB64, setEditPhotoB64] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [saving, setSaving]       = useState(false);
  const editFileRef               = useRef(null);

  // Add player state
  const [addOpen, setAddOpen]           = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]       = useState(false);
  const [addingSports, setAddingSports] = useState([]);
  const [adding, setAdding]             = useState(false);
  const searchDebounce                  = useRef(null);

  useEffect(() => { fetchAll(); fetchTeamAssignments(); fetchKOResults(); }, [teamId, orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute sport achievements from KO encounters
  const fetchKOResults = async () => {
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/scorer/encounters`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      if (!res.ok) return;
      const data = await res.json();
      const achievements = {};
      (Array.isArray(data) ? data : [])
        .filter(e => ['sf','final','3rd'].includes(e.round_type) && e.winner &&
                     (e.home_team_id === teamId || e.away_team_id === teamId))
        .forEach(e => {
          const sc = e.sport_code || (e.sport_results?.[0]?.sport_code);
          if (!sc) return;
          const side = e.home_team_id === teamId ? 'home' : 'away';
          const won  = e.winner === side;
          if (e.round_type === 'final') {
            achievements[sc] = won ? 'winner' : 'runner_up';
          } else if (e.round_type === 'sf' && !won && !achievements[sc]) {
            achievements[sc] = 'semi';
          }
        });
      setKoAchievements(achievements);
    } catch (_) {}
  };

  // Fetch team encounters (matches) when Matches tab is activated
  const fetchTeamEncounters = async () => {
    if (teamEncounters.length > 0) return; // already loaded
    setEncLoading(true);
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/match-scores`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      if (res.ok) {
        const data = await res.json();
        // Filter matches to only those involving this team
        const teamMatches = (data.matches || []).filter(m =>
          m.home_team_id === teamId || m.away_team_id === teamId
        );
        // Group by encounter_id + sport_code
        const encMap = {};
        teamMatches.forEach(m => {
          const key = m.encounter_id;
          if (!encMap[key]) encMap[key] = {
            encounter_id: m.encounter_id,
            tournament_name: m.tournament_name,
            match_label: m.match_label,
            round_type: m.round_type,
            scheduled_at: m.scheduled_at,
            home_team_name: m.home_team_name,
            away_team_name: m.away_team_name,
            home_team_color: m.home_team_color,
            away_team_color: m.away_team_color,
            sports: {},
          };
          const sc = m.sport_code;
          if (!encMap[key].sports[sc]) encMap[key].sports[sc] = [];
          encMap[key].sports[sc].push(m);
        });
        setTeamEncounters(Object.values(encMap).sort((a,b) =>
          (b.scheduled_at||'').localeCompare(a.scheduled_at||'')
        ));
      }
    } catch (_) { toast.error('Failed to load matches'); }
    finally { setEncLoading(false); }
  };

  const fetchTeamAssignments = async () => {
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/player-team-assignments`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      if (res.ok) setTeamAssignments(await res.json());
    } catch (_) {}
  };

  const handleRemovePlayer = async (entry) => {
    const name = entry.player_name || entry.player?.full_name || 'this player';
    if (!confirm(`Remove ${name} from the team?`)) return;
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/teams/${teamId}/roster/${entry.player_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token()}` },
      });
      if (res.ok) { toast.success(`${name} removed`); setSelectedIds(new Set()); fetchAll(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed to remove'); }
    } catch (_) { toast.error('Failed to remove player'); }
  };

  // Transfer player to another team
  const [transferTarget, setTransferTarget]   = useState(null); // roster entry
  const [transferTeamId, setTransferTeamId]   = useState('');
  const [transferring, setTransferring]       = useState(false);
  const [allTeams, setAllTeams]               = useState([]);

  const openTransfer = async (entry) => {
    setTransferTarget(entry);
    setTransferTeamId('');
    if (!allTeams.length) {
      try {
        const res = await fetch(`/api/orgs/${orgSlug}/teams`, { headers: { 'Authorization': `Bearer ${token()}` } });
        if (res.ok) setAllTeams((await res.json()).filter(t => t.id !== teamId));
      } catch (_) {}
    }
  };

  const confirmTransfer = async () => {
    if (!transferTarget || !transferTeamId) return;
    setTransferring(true);
    const name = transferTarget.player_name || 'Player';
    const pid  = transferTarget.player_id;
    try {
      // Step 1: Add to destination FIRST (safer — fails before removing)
      const addToken = localStorage.getItem('btccp_access_token');
      const add = await fetch(`/api/orgs/${orgSlug}/teams/${transferTeamId}/roster`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${addToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: pid }),   // singular player_id (not array)
      });
      if (!add.ok) {
        const d = await add.json().catch(() => ({}));
        toast.error(`Transfer failed: ${d.detail || 'Could not add to destination team'}`);
        setTransferring(false);
        return;
      }
      // Step 2: Remove from current team (only after successful add)
      const removeToken = localStorage.getItem('btccp_access_token');
      const del = await fetch(`/api/orgs/${orgSlug}/teams/${teamId}/roster/${pid}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${removeToken}` },
      });
      if (del.ok) {
        toast.success(`${name} transferred successfully`);
        setTransferTarget(null);
        fetchAll();
      } else {
        // Add succeeded but remove failed — undo the add
        await fetch(`/api/orgs/${orgSlug}/teams/${transferTeamId}/roster/${pid}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${removeToken}` },
        });
        toast.error('Transfer failed — player remains in original team');
      }
    } catch (_) { toast.error('Transfer failed'); }
    finally { setTransferring(false); }
  };

  const handleRosterPatch = async (playerId, patch) => {
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/teams/${teamId}/roster/${playerId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        setRoster(prev => prev.map(r =>
          r.player_id === playerId ? { ...r, ...updated } : (patch.is_sponsor ? { ...r, is_sponsor: false } : r)
        ));
      } else toast.error('Failed to update');
    } catch (_) { toast.error('Failed to update'); }
  };

  const handlePlayerSearch = (q) => {
    setPlayerSearch(q);
    clearTimeout(searchDebounce.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`/api/orgs/${orgSlug}/players?q=${encodeURIComponent(q.trim())}&limit=20`,
          { headers: { 'Authorization': `Bearer ${token()}` } });
        const data = await res.json();
        // Exclude: already in this team OR already in any other team
        const inThisTeam = new Set(roster.map(r => r.player_id));
        const inAnyTeam  = new Set(Object.keys(teamAssignments));
        setSearchResults((Array.isArray(data) ? data : []).filter(p =>
          !inThisTeam.has(p.id) && !inAnyTeam.has(p.id)
        ));
      } catch (_) {}
      finally { setSearching(false); }
    }, 300);
  };

  const handleAddPlayer = async (player) => {
    setAdding(true);
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/teams/${teamId}/roster`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: player.id, sports: addingSports, role_in_team: 'player' }),
      });
      if (res.ok) {
        toast.success(`${player.full_name || player.first_name} added to team`);
        setPlayerSearch(''); setSearchResults([]); setAddingSports([]);
        setAddOpen(false);
        fetchAll();
        fetchTeamAssignments(); // refresh so they won't show in search again
      } else {
        const d = await res.json(); toast.error(d.detail || 'Failed to add');
      }
    } catch (_) { toast.error('Failed to add player'); }
    finally { setAdding(false); }
  };

  const toggleAddSport = (code) =>
    setAddingSports(prev => prev.includes(code) ? prev.filter(s=>s!==code) : [...prev, code]);

  const toggleSelect = (pid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(pid) ? next.delete(pid) : next.add(pid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === roster.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(roster.map(r => r.player_id)));
    }
  };

  const handleBulkRemove = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!confirm(`Remove ${count} player${count > 1 ? 's' : ''} from ${team?.name}? This cannot be undone.`)) return;
    setBulkRemoving(true);
    try {
      await Promise.all([...selectedIds].map(pid =>
        fetch(`/api/orgs/${orgSlug}/teams/${teamId}/roster/${pid}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token()}` },
        })
      ));
      toast.success(`${count} player${count > 1 ? 's' : ''} removed`);
      setSelectedIds(new Set());
      fetchAll();
    } catch (_) { toast.error('Some removals failed'); }
    finally { setBulkRemoving(false); }
  };
  useEffect(() => {
    if (searchParams.get('edit') === '1' && team) {
      openEdit();
      navigate(`/admin/teams/${teamId}`, { replace: true }); // clear ?edit=1 to prevent reopen loop
    }
  }, [team]);
  useEffect(() => { if (selectedTid) fetchAuctionPicks(); }, [selectedTid]);

  const fetchAll = async () => {
    setLoading(true);
    const hdrs = { 'Authorization': `Bearer ${token()}` };
    try {
      const [teamRes, rosterRes, sportsRes, catsRes, tournsRes] = await Promise.all([
        fetch(`/api/orgs/${orgSlug}/teams/${teamId}`, { headers: hdrs }).then(r => r.json()),
        fetch(`/api/orgs/${orgSlug}/teams/${teamId}/roster`, { headers: hdrs }).then(r => r.json()),
        fetch(`/api/orgs/${orgSlug}/sports`, { headers: hdrs }).then(r => r.json()),
        fetch(`/api/orgs/${orgSlug}/player-categories`, { headers: hdrs }).then(r => r.json()),
        fetch(`/api/orgs/${orgSlug}/tournaments`, { headers: hdrs }).then(r => r.json()),
      ]);
      setTeam(teamRes);
      setRoster(Array.isArray(rosterRes) ? rosterRes : []);
      setSports(Array.isArray(sportsRes) ? sportsRes : []);
      setCategories(Array.isArray(catsRes) ? catsRes : []);
      const tourns = Array.isArray(tournsRes) ? tournsRes : [];
      setTournaments(tourns);
      if (tourns.length > 0) {
        setSelectedTid(tourns[0].id);
        // Fetch sport formats to get platinum_count limits
        try {
          const fmtRes = await fetch(`/api/orgs/${orgSlug}/tournaments/${tourns[0].id}/sport-formats`, { headers: hdrs });
          if (fmtRes.ok) {
            const fmts = await fmtRes.json();
            const map = {};
            (Array.isArray(fmts)?fmts:[]).forEach(f => { map[f.sport_code] = f; });
            setSportFormats(map);
          }
        } catch (_) {}
      }
    } catch (err) {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuctionPicks = async () => {
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${selectedTid}/auction/picks`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      const picks = await res.json();
      if (Array.isArray(picks)) {
        const map = {};
        picks.forEach(p => { if (p.player_id) map[p.player_id] = p.bid_amount || 0; });
        setAuctionPicks(map);
      }
    } catch (_) {}
  };

  const getCategoryConfig = (categoryCode) => {
    if (CATEGORY_CONFIG[categoryCode]) return CATEGORY_CONFIG[categoryCode];
    const cat = categories.find(c => c.code === categoryCode);
    if (cat) return { label: cat.name, icon: cat.is_platinum ? '👑' : '⭐', color: cat.color };
    return { label: 'Advanced', icon: '⭐', color: '#f5c518' };
  };

  const openEdit = () => {
    setEditForm({ name: team?.name || '', short_name: team?.short_name || '', color: team?.color || '#3b82f6' });
    setEditLogoPreview(team?.logo_url || null);
    setEditLogoB64(null);
    setEditPhotoPreview(team?.team_photo_url || null);
    setEditPhotoB64(null);
    setEditOpen(true);
  };

  const handleEditLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setEditLogoPreview(ev.target.result); setEditLogoB64(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSaveTeam = async () => {
    if (!editForm.name.trim()) { toast.error('Team name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name:       editForm.name.trim(),
        short_name: editForm.short_name.trim().slice(0, 5) || undefined,
        color:      editForm.color,
        ...(editLogoB64  ? { logo_b64:       editLogoB64 }  : {}),
        ...(editPhotoB64 ? { team_photo_b64: editPhotoB64 } : {}),
      };
      const res = await fetch(`/api/orgs/${orgSlug}/teams/${teamId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.detail || 'Failed to update team'); return; }
      toast.success('Team updated!');
      setTeam(prev => ({ ...prev, ...data }));
      setEditOpen(false);
      fetchAll();
    } catch (err) {
      toast.error('Failed to update team');
    } finally {
      setSaving(false);
    }
  };

  // Group players by sport
  const sportGroups = sports.reduce((acc, sp) => {
    const players = roster.filter(r => (r.sports || []).includes(sp.code));
    if (players.length > 0) acc[sp.code] = { sport: sp, players };
    return acc;
  }, {});

  // Players with no sport assigned
  const unassignedPlayers = roster.filter(r => !r.sports || r.sports.length === 0);

  const PlayerCard = ({ entry }) => {
    const player  = entry.player || {};
    const pid     = entry.player_id;
    const name    = entry.player_name || player.full_name || `Player ${pid?.slice(-6)}`;
    const catCode = player.category || 'advanced';
    const catCfg  = getCategoryConfig(catCode);
    const bid     = auctionPicks[pid] || entry.bid_amount || 0;
    const checked       = selectedIds.has(pid);
    const platSports    = entry.platinum_sports || (entry.is_platinum ? [] : []);  // per-sport array
    const isSponsor     = entry.is_sponsor === true;
    const isCaptainBadge = team?.captain_player_id === pid;
    const playerSports  = entry.sports || player.sports || [];

    const toggleSportPlatinum = (sportCode) => {
      const current = entry.platinum_sports || [];
      const updated = current.includes(sportCode)
        ? current.filter(s => s !== sportCode)
        : [...current, sportCode];
      handleRosterPatch(pid, { platinum_sports: updated });
    };

    const SPORT_ICONS_PLAYER = { table_tennis:'🏓', badminton:'🏸', chess:'♟', carrom:'🎯', pickleball:'🥒', squash:'🎾' };
    const SPORT_SHORT        = { table_tennis:'TT', badminton:'BD', chess:'CH', carrom:'CR', pickleball:'PB', squash:'SQ' };

    return (
      <div className={`flex items-center gap-2 py-3 px-4 border-b border-[#1e2435] last:border-0 transition-colors group ${
        checked ? 'bg-[rgba(239,68,68,0.04)]' : 'hover:bg-[rgba(255,255,255,0.02)]'
      }`}>
        {/* Checkbox — admin only */}
        {admin && (
        <input type="checkbox" checked={checked} onChange={() => toggleSelect(pid)}
          onClick={e => e.stopPropagation()}
          className="h-4 w-4 rounded border-[#252a37] bg-[#1d2230] accent-[#ef4444] flex-shrink-0 cursor-pointer"
          data-testid={`player-checkbox-${pid}`}/>
        )}

        {/* Avatar */}
        <div className="h-11 w-11 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center font-bold text-lg relative"
          style={{ background: `linear-gradient(135deg, ${catCfg.color}40, ${catCfg.color}20)`, color: catCfg.color }}>
          {player.photo_url
            ? <img src={player.photo_url} className="h-full w-full object-cover" alt={name}/>
            : name.split(' ').map(n => n[0]).slice(0,2).join('')
          }
          {platSports.length > 0 && (
            <span className="absolute -top-1 -right-1 text-[10px] bg-[#f5c518] rounded-full px-1 leading-4 font-bold text-[#0b0f18]">P</span>
          )}
        </div>

        {/* Name + badges */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 flex-wrap">
            <p className="font-semibold text-[#f0f2f7] truncate max-w-full">
              {isCaptainBadge && <span className="text-[#f5c518] mr-1" title="Captain">👑</span>}
              {name}
            </p>
            {/* Per-sport platinum badges — bigger, show icon + short label */}
            {platSports.map(sc => (
              <span key={sc} className="inline-flex items-center gap-0.5 text-xs bg-[rgba(245,197,24,0.15)] text-[#f5c518] border border-[rgba(245,197,24,0.4)] px-2 py-0.5 rounded-full font-black">
                <span className="text-sm">{SPORT_ICONS_PLAYER[sc]||'★'}</span>
                <span>{SPORT_SHORT[sc]||sc.slice(0,2).toUpperCase()}</span>
                <span>★</span>
              </span>
            ))}
            {isSponsor && <span className="text-[10px] bg-[rgba(34,197,94,0.12)] text-[#22c55e] border border-[rgba(34,197,94,0.3)] px-1.5 py-0.5 rounded font-semibold">Sponsor</span>}
          </div>
          <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: catCfg.color }}>
            <span>{catCfg.icon}</span><span>{catCfg.label}</span>
          </p>
        </div>

        {/* Bid amount */}
        {bid > 0 && <span className="text-sm font-bold flex-shrink-0 hidden sm:inline" style={{ color:'#f5c518' }}>₹{Number(bid).toLocaleString('en-IN')}</span>}
        {entry.jersey_number && (
          <div className="h-7 w-7 rounded-full bg-[#1d2230] flex items-center justify-center text-[#8a93a3] text-xs font-bold flex-shrink-0 hidden sm:flex">
            {entry.jersey_number}
          </div>
        )}

        {/* Per-sport platinum toggles — admin only, desktop only */}
        {admin && playerSports.length > 0 && (
          <div className="hidden sm:flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {playerSports.map(sc => {
              const isPlatForSport = platSports.includes(sc);
              const fmt = sportFormats[sc];
              const maxPlat = fmt?.platinum_count ?? 2;
              // Count current platinum players for this sport in the team
              const currentPlatCount = roster.filter(r =>
                r.player_id !== pid && (r.platinum_sports || []).includes(sc)
              ).length;
              const limitReached = !isPlatForSport && currentPlatCount >= maxPlat;

              return (
                <button key={sc}
                  onClick={() => {
                    if (limitReached) {
                      toast.error(`Platinum limit for ${sc.replace(/_/g,' ')} is ${maxPlat} players (already filled)`);
                      return;
                    }
                    toggleSportPlatinum(sc);
                  }}
                  title={limitReached
                    ? `Limit: ${maxPlat} platinum players for ${sc.replace(/_/g,' ')} (all filled)`
                    : `${isPlatForSport ? 'Remove' : 'Set'} platinum for ${sc.replace(/_/g,' ')}`}
                  className={`flex items-center gap-0.5 h-7 px-2 rounded-lg text-xs font-bold transition-all border ${
                    isPlatForSport
                      ? 'bg-[rgba(245,197,24,0.2)] text-[#f5c518] border-[rgba(245,197,24,0.5)]'
                      : limitReached
                        ? 'bg-[#1d2230] text-[#8a93a3]/40 border-[#252a37] cursor-not-allowed'
                        : 'bg-[#1d2230] text-[#8a93a3] border-[#252a37] hover:border-[rgba(245,197,24,0.3)] hover:text-[#f5c518]'
                  }`}
                  data-testid={`platinum-${sc}-${pid}`}
                >
                  <span className="text-base">{SPORT_ICONS_PLAYER[sc]||'★'}</span>
                  <span>{SPORT_SHORT[sc]||sc.slice(0,2).toUpperCase()}</span>
                  {isPlatForSport ? <span className="text-[#f5c518]">★</span> : <span className="text-[10px] opacity-60">{currentPlatCount}/{maxPlat}</span>}
                </button>
              );
            })}
          </div>
        )}
        {/* Fallback platinum toggle — admin only, desktop */}
        {admin && playerSports.length === 0 && (
          <button
            onClick={() => handleRosterPatch(pid, { is_platinum: !(entry.is_platinum), platinum_sports: entry.is_platinum ? [] : ['all'] })}
            className={`hidden sm:flex h-7 px-2 rounded-lg text-[10px] font-bold flex-shrink-0 transition-all border items-center opacity-0 group-hover:opacity-100 ${
              entry.is_platinum
                ? 'bg-[rgba(245,197,24,0.15)] text-[#f5c518] border-[rgba(245,197,24,0.4)]'
                : 'bg-[#1d2230] text-[#8a93a3] border-[#252a37] hover:border-[rgba(245,197,24,0.3)] hover:text-[#f5c518]'
            }`}
            data-testid={`platinum-toggle-${pid}`}
          >
            {entry.is_platinum ? '★ P' : '☆ P'}
          </button>
        )}

        {/* Sponsor toggle — admin only, desktop only */}
        {admin && (
        <button
          onClick={() => handleRosterPatch(pid, { is_sponsor: !isSponsor })}
          className={`hidden sm:flex h-7 px-2 rounded-lg text-[10px] font-bold flex-shrink-0 transition-all border items-center ${
            isSponsor
              ? 'bg-[rgba(34,197,94,0.12)] text-[#22c55e] border-[rgba(34,197,94,0.3)] hover:bg-[rgba(34,197,94,0.2)]'
              : 'bg-[#1d2230] text-[#8a93a3] border-[#252a37] hover:border-[rgba(34,197,94,0.3)] hover:text-[#22c55e] opacity-0 group-hover:opacity-100'
          }`}
          title={isSponsor ? 'Remove as Sponsor' : 'Mark as Team Sponsor'}
          data-testid={`sponsor-toggle-${pid}`}
        >
          {isSponsor ? '🏆 S' : '○ S'}
        </button>
        )}

        {/* Captain toggle — admin only */}
        {admin && (() => {
          const isCaptain = team?.captain_player_id === pid;
          return (
            <button
              onClick={async () => {
                const newCaptainId = isCaptain ? null : pid;
                try {
                  const token = localStorage.getItem('btccp_access_token');
                  const res = await fetch(`/api/orgs/${orgSlug}/teams/${teamId}`, {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ captain_player_id: newCaptainId }),
                  });
                  if (res.ok) {
                    setTeam(prev => ({ ...prev, captain_player_id: newCaptainId }));
                    toast.success(isCaptain ? 'Captain removed' : `${entry.player_name || 'Player'} set as captain`);
                  } else toast.error('Failed to update captain');
                } catch (_) { toast.error('Failed to update captain'); }
              }}
              className={`hidden sm:flex h-7 px-2 rounded-lg text-[10px] font-bold flex-shrink-0 transition-all border items-center ${
                isCaptain
                  ? 'bg-[rgba(245,197,24,0.15)] text-[#f5c518] border-[rgba(245,197,24,0.4)]'
                  : 'bg-[#1d2230] text-[#8a93a3] border-[#252a37] hover:border-[rgba(245,197,24,0.3)] hover:text-[#f5c518] opacity-0 group-hover:opacity-100'
              }`}
              title={isCaptain ? 'Remove as Captain' : 'Mark as Captain'}
              data-testid={`captain-toggle-${pid}`}
            >
              {isCaptain ? '👑 C' : '○ C'}
            </button>
          );
        })()}

        {/* Remove single — admin only, desktop */}
        {admin && (
        <button onClick={() => handleRemovePlayer(entry)}
          className="hidden sm:flex h-7 w-7 rounded-full items-center justify-center text-[#8a93a3] hover:text-[#ef4444] hover:bg-[rgba(239,68,68,0.1)] transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
          title="Remove from team" data-testid="remove-player-btn">
          <Trash2 className="h-3.5 w-3.5"/>
        </button>
        )}
        {/* Transfer — admin only, desktop */}
        {admin && (
        <button onClick={() => openTransfer(entry)}
          className="hidden sm:flex h-7 px-1.5 rounded-lg items-center justify-center text-[10px] font-bold text-[#8a93a3] hover:text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)] border border-transparent hover:border-[rgba(245,197,24,0.3)] transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100 gap-1"
          title="Transfer to another team" data-testid={`transfer-player-${pid}`}>
          <ArrowLeftRight className="h-3.5 w-3.5"/>
        </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <Skeleton className="h-8 w-48 bg-[#1d2230] mb-6"/>
        <div className="space-y-3">{[1,2,3].map(i=><Skeleton key={i} className="h-16 bg-[#1d2230]"/>)}</div>
      </div>
    );
  }

  if (!team) return (
    <div className="p-6 text-center">
      <p className="text-[#8a93a3]">Team not found</p>
      <Button onClick={() => navigate('/admin/teams')} className="mt-4">Back to Teams</Button>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="team-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/teams')}
          className="text-[#8a93a3] hover:text-[#f0f2f7]">
          <ArrowLeft className="h-5 w-5"/>
        </Button>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Team logo/color */}
          <div className="h-10 w-10 rounded-xl flex items-center justify-center font-bold text-white text-lg flex-shrink-0 overflow-hidden"
            style={{ backgroundColor: team.color || '#3b82f6' }}>
            {team.logo_url?.startsWith('data:')
              ? <img src={team.logo_url} className="h-full w-full object-cover" alt={team.name} />
              : team.short_name?.[0] || team.name?.[0]
            }
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#f5c518]">{team.name}</h1>
            <p className="text-xs text-[#8a93a3]">
              {team.short_name && <span>{team.short_name} · </span>}
              {roster.length} player{roster.length !== 1 ? 's' : ''}
              {roster.filter(r => r.is_platinum).length > 0 && (
                <span className="text-[#f5c518] ml-1.5">· {roster.filter(r => r.is_platinum).length} platinum</span>
              )}
              {roster.find(r => r.is_sponsor) && (
                <span className="text-[#22c55e] ml-1.5">· sponsor set</span>
              )}
            </p>
            {/* Sport achievement badges */}
            {Object.keys(koAchievements).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {Object.entries(koAchievements).map(([sc, result]) => {
                  const cfg = {
                    winner:    { icon:'🏆', label:'Winner',      color:'#f5c518', bg:'rgba(245,197,24,0.12)',  border:'rgba(245,197,24,0.4)' },
                    runner_up: { icon:'🥈', label:'Runner-up',   color:'#94a3b8', bg:'rgba(148,163,184,0.1)', border:'rgba(148,163,184,0.35)' },
                    semi:      { icon:'🥉', label:'Semi-finalist',color:'#fb923c', bg:'rgba(251,146,60,0.1)',  border:'rgba(251,146,60,0.35)' },
                  }[result] || {};
                  const sportName = sc.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
                  return (
                    <span key={sc}
                      className="text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap"
                      style={{ color: cfg.color, backgroundColor: cfg.bg, border: `1px solid ${cfg.border}` }}>
                      {cfg.icon} {cfg.label} · {sportName}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAll} className="text-[#8a93a3]">
          <RefreshCw className="h-4 w-4"/>
        </Button>
        {admin && (
          <>
            <Button size="sm" onClick={() => { setAddOpen(true); setPlayerSearch(''); setSearchResults([]); setAddingSports([]); }}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="add-player-to-team-btn">
              <Plus className="h-4 w-4 mr-1.5"/>Add Player
            </Button>
            <Button variant="outline" size="sm" onClick={openEdit}
              className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
              data-testid="edit-team-button">
              <Edit2 className="h-4 w-4 mr-1.5"/>Edit
            </Button>
          </>
        )}
      </div>

      {/* Team Group Photo — displayed above tabs when available */}
      {team.team_photo_url && (
        <div className="relative mb-5 rounded-2xl overflow-hidden border border-[#252a37] group">
          <img src={team.team_photo_url} alt={`${team.name} team photo`}
            className="w-full object-cover max-h-64"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f18]/70 to-transparent pointer-events-none"/>
          <p className="absolute bottom-2 right-3 text-[10px] text-white/60">Team Photo</p>
          {admin && (
            <button onClick={openEdit}
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#0b0f18]/70 text-[#8a93a3] hover:text-[#f5c518] rounded-lg p-1.5">
              <Camera className="h-3.5 w-3.5"/>
            </button>
          )}
        </div>
      )}

      {/* Tournament selector for bid amounts */}
      {tournaments.length > 0 && (
        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs text-[#8a93a3] flex-shrink-0">Bid amounts from:</span>
          <Select value={selectedTid} onValueChange={setSelectedTid}>
            <SelectTrigger className="bg-[#161a26] border-[#252a37] text-[#f0f2f7] text-sm h-8 flex-1 max-w-[250px]">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="bg-[#161a26] border-[#252a37]">
              {tournaments.map(t => <SelectItem key={t.id} value={t.id} className="text-[#f0f2f7] focus:bg-[#1d2230]">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Tab toggle — scrollable on mobile */}
      <div className="flex bg-[#161a26] rounded-xl p-1 mb-6 border border-[#252a37] overflow-x-auto scrollbar-none gap-0.5">
        <button onClick={() => { setActiveTab('list'); setSelectedIds(new Set()); }}
          className={`flex-1 min-w-[90px] flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'list'
              ? 'bg-[#f5c518] text-[#0b0f18]'
              : 'text-[#8a93a3] hover:text-[#f0f2f7]'
          }`}
          data-testid="tab-player-list">
          <List className="h-3.5 w-3.5 flex-shrink-0"/>
          Player List
        </button>
        <button onClick={() => { setActiveTab('platinum'); setSelectedIds(new Set()); }}
          className={`flex-1 min-w-[80px] flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'platinum'
              ? 'bg-[#f5c518] text-[#0b0f18]'
              : 'text-[#8a93a3] hover:text-[#f0f2f7]'
          }`}
          data-testid="tab-platinum">
          <Star className="h-3.5 w-3.5 flex-shrink-0"/>
          Platinum
        </button>
        <button onClick={() => { setActiveTab('sport'); setSelectedIds(new Set()); }}
          className={`flex-1 min-w-[90px] flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'sport'
              ? 'bg-[#f5c518] text-[#0b0f18]'
              : 'text-[#8a93a3] hover:text-[#f0f2f7]'
          }`}
          data-testid="tab-sport-wise">
          <Grid3x3 className="h-3.5 w-3.5 flex-shrink-0"/>
          Sport Wise
        </button>
        <button onClick={() => { setActiveTab('matches'); fetchTeamEncounters(); }}
          className={`flex-1 min-w-[75px] flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'matches'
              ? 'bg-[#f5c518] text-[#0b0f18]'
              : 'text-[#8a93a3] hover:text-[#f0f2f7]'
          }`}
          data-testid="tab-matches">
          <Trophy className="h-3.5 w-3.5 flex-shrink-0"/>
          Matches
        </button>
      </div>

      {/* Bulk select bar */}
      {roster.length > 0 && (
        <>
          {/* Platinum status per sport */}
          {Object.keys(sportFormats).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2 px-1">
              {Object.entries(sportFormats).filter(([,f])=>f.platinum_count>0).map(([sc,fmt])=>{
                const count = roster.filter(r=>(r.platinum_sports||[]).includes(sc)).length;
                const max   = fmt.platinum_count;
                const full  = count >= max;
                const ICONS = {table_tennis:'🏓',badminton:'🏸',chess:'♟️',carrom:'🎯',pickleball:'🥒'};
                return (
                  <span key={sc} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
                    full?'bg-[rgba(34,197,94,0.1)] text-[#22c55e] border-[rgba(34,197,94,0.3)]'
                        :'bg-[rgba(245,197,24,0.06)] text-[#f5c518] border-[rgba(245,197,24,0.2)]'
                  }`}>
                    {ICONS[sc]||'★'} {sc.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}: {count}/{max}{full?' ✓':''}
                  </span>
                );
              })}
            </div>
          )}
        {/* Bulk select bar — admin only */}
        {admin && (
        <div className={`flex items-center gap-3 mb-3 px-1 transition-all ${selectedIds.size > 0 ? 'opacity-100' : 'opacity-60'}`}>
          <input type="checkbox"
            checked={selectedIds.size === roster.length && roster.length > 0}
            ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < roster.length; }}
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded border-[#252a37] bg-[#1d2230] accent-[#ef4444] cursor-pointer flex-shrink-0"
            data-testid="select-all-checkbox"/>
          <span className="text-sm text-[#8a93a3] flex-1">
            {selectedIds.size > 0
              ? <span className="text-[#f0f2f7] font-medium">{selectedIds.size} of {roster.length} selected</span>
              : <span>Select all · <span className="text-[10px] text-[#8a93a3]">Hover a player to set <span className="text-[#f5c518]">★P</span> Platinum or <span className="text-[#22c55e]">S</span> Sponsor</span></span>}
          </span>
          {selectedIds.size > 0 && (
            <Button onClick={handleBulkRemove} disabled={bulkRemoving} size="sm"
              className="bg-[rgba(239,68,68,0.15)] text-[#ef4444] hover:bg-[rgba(239,68,68,0.25)] border border-[rgba(239,68,68,0.3)] h-8 text-xs"
              data-testid="bulk-remove-button">
              <Trash2 className="h-3.5 w-3.5 mr-1.5"/>
              {bulkRemoving ? 'Removing...' : `Remove ${selectedIds.size} Player${selectedIds.size > 1 ? 's' : ''}`}
            </Button>
          )}
        </div>
        )}
        </>
      )}

      {/* PLAYER LIST tab */}
      {/* PLATINUM PLAYERS tab */}
      {activeTab === 'platinum' && (() => {
        const SPORTS = ['table_tennis','badminton','chess','carrom','pickleball'];
        const SPORT_ICONS = { table_tennis:'🏓', badminton:'🏸', chess:'♙', carrom:'🎯', pickleball:'🥒' };
        const SPORT_NAMES = { table_tennis:'Table Tennis', badminton:'Badminton', chess:'Chess', carrom:'Carrom', pickleball:'Pickleball' };
        // Build map: sport → platinum players
        const platBySport = {};
        SPORTS.forEach(sc => {
          platBySport[sc] = roster.filter(r => (r.platinum_sports || []).includes(sc));
        });
        const totalPlat = Object.values(platBySport).flat().length;
        return (
          <div className="space-y-4">
            {/* Summary chips */}
            <div className="flex flex-wrap gap-2 mb-2">
              {SPORTS.map(sc => {
                const count = platBySport[sc].length;
                return (
                  <span key={sc} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border ${
                    count > 0
                      ? 'bg-[rgba(245,197,24,0.12)] border-[rgba(245,197,24,0.4)] text-[#f5c518]'
                      : 'bg-[#161a26] border-[#252a37] text-[#8a93a3]'
                  }`}>
                    {SPORT_ICONS[sc]} {SPORT_NAMES[sc]}: {count}
                  </span>
                );
              })}
              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[rgba(245,197,24,0.05)] border border-[rgba(245,197,24,0.2)] text-[#f5c518]">
                ★ Total: {totalPlat}
              </span>
            </div>

            {/* Sport sections */}
            {SPORTS.map(sc => {
              const players = platBySport[sc];
              if (players.length === 0) return null;
              return (
                <Card key={sc} className="bg-[#10131c] border-[rgba(245,197,24,0.2)] overflow-hidden">
                  <CardContent className="p-0">
                    {/* Sport header */}
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[rgba(245,197,24,0.05)] border-b border-[rgba(245,197,24,0.15)]">
                      <span className="text-sm font-black text-[#f5c518] flex items-center gap-2">
                        <span className="text-lg">{SPORT_ICONS[sc]}</span>
                        {SPORT_NAMES[sc]}
                      </span>
                      <span className="text-xs font-bold text-[#f5c518] bg-[rgba(245,197,24,0.15)] px-2 py-0.5 rounded-full">
                        {players.length} platinum
                      </span>
                    </div>
                    {/* Player rows */}
                    <div className="divide-y divide-[#1e2435]/60">
                      {players.map((r, i) => {
                        const player = roster.find(p => p.player_id === r.player_id) || r;
                        const name = r.player_name || player.player_name || `Player ${i+1}`;
                        return (
                          <div key={r.player_id} className="flex items-center gap-3 px-4 py-2.5">
                            {/* Avatar */}
                            <div className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-bold text-white text-sm overflow-hidden"
                              style={{ backgroundColor: team.color || '#3b82f6' }}>
                              {r.photo_url?.startsWith('data:')
                                ? <img src={r.photo_url} className="h-full w-full object-cover" alt={name}/>
                                : name[0]?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[#f0f2f7] truncate">{name}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-[10px] text-[#8a93a3]">
                                  {r.category || 'Advanced'}
                                </span>
                              </div>
                            </div>
                            <span className="text-[#f5c518] font-black text-sm">★ Platinum</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {totalPlat === 0 && (
              <Card className="bg-[#161a26] border-[#252a37]">
                <CardContent className="p-10 text-center">
                  <Star className="h-10 w-10 text-[#8a93a3] mx-auto mb-3"/>
                  <p className="text-[#f0f2f7] font-semibold">No platinum players yet</p>
                  <p className="text-sm text-[#8a93a3] mt-1">Go to Sport Wise tab to assign platinum players</p>
                </CardContent>
              </Card>
            )}
          </div>
        );
      })()}

      {activeTab === 'list' && (
        <Card className="bg-[#10131c] border-[#252a37] overflow-hidden">
          {roster.length === 0 ? (
            <CardContent className="p-10 text-center">
              <Users className="h-12 w-12 text-[#8a93a3] mx-auto mb-3"/>
              <p className="text-[#f0f2f7] font-medium">No players assigned yet</p>
              <p className="text-sm text-[#8a93a3] mt-1">Add players from the Registration tab in Tournament Details</p>
            </CardContent>
          ) : (
            roster.map((entry, i) => <PlayerCard key={entry.id || i} entry={entry}/>)
          )}
        </Card>
      )}

      {/* SPORT WISE tab */}
      {activeTab === 'sport' && (
        <div className="space-y-4">
          {Object.keys(sportGroups).length === 0 && unassignedPlayers.length === 0 ? (
            <Card className="bg-[#10131c] border-[#252a37]">
              <CardContent className="p-10 text-center">
                <Trophy className="h-12 w-12 text-[#8a93a3] mx-auto mb-3"/>
                <p className="text-[#f0f2f7] font-medium">No sport assignments yet</p>
                <p className="text-sm text-[#8a93a3] mt-1">Assign players to sports when adding them to the roster</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {Object.entries(sportGroups).map(([code, { sport, players }]) => (
                <Card key={code} className="bg-[#10131c] border-[#252a37] overflow-hidden">
                  {/* Sport header */}
                  <div className="flex items-center px-4 py-3 border-b border-[#1e2435]">
                    <div className="flex items-center gap-2.5">
                      <SportIcon sport={code} size={20}/>
                      <span className="font-bold text-[#f5c518] text-base">
                        {sport.name} <span className="text-[#8a93a3] font-semibold">({players.length})</span>
                      </span>
                    </div>
                  </div>
                  {players.map((entry, i) => <PlayerCard key={entry.id || i} entry={entry}/>)}
                </Card>
              ))}

              {/* Unassigned players */}
              {unassignedPlayers.length > 0 && (
                <Card className="bg-[#10131c] border-[#252a37] overflow-hidden">
                  <div className="flex items-center px-4 py-3 border-b border-[#1e2435]">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xl">🎯</span>
                      <span className="font-bold text-[#8a93a3] text-base">
                        No Sport Assigned <span className="text-[#8a93a3]/60 font-semibold">({unassignedPlayers.length})</span>
                      </span>
                    </div>
                  </div>
                  {unassignedPlayers.map((entry, i) => <PlayerCard key={entry.id || i} entry={entry}/>)}
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* MATCHES tab */}
      {activeTab === 'matches' && (
        <div className="space-y-3">
          {encLoading ? (
            <div className="space-y-3">{[1,2,3].map(i=><div key={i} className="h-20 bg-[#161a26] rounded-xl animate-pulse"/>)}</div>
          ) : teamEncounters.length === 0 ? (
            <Card className="bg-[#10131c] border-[#252a37]">
              <CardContent className="p-10 text-center">
                <Trophy className="h-10 w-10 text-[#8a93a3] mx-auto mb-3"/>
                <p className="text-[#f0f2f7] font-semibold">No matches found</p>
                <p className="text-sm text-[#8a93a3] mt-1">This team has not played any scored matches yet</p>
              </CardContent>
            </Card>
          ) : teamEncounters.map(enc => {
            const isOpen = openEncId === enc.encounter_id;
            const isHome = enc.home_team_name && enc.home_team_name === team?.name;
            const myName  = isHome ? enc.home_team_name  : enc.away_team_name;
            const oppName = isHome ? enc.away_team_name  : enc.home_team_name;
            const myColor  = isHome ? enc.home_team_color : enc.away_team_color;
            const oppColor = isHome ? enc.away_team_color : enc.home_team_color;
            const sports = Object.entries(enc.sports);
            // compute win/loss for this team across sport results
            const myWins = sports.reduce((s,[,matches]) =>
              s + matches.filter(m => (isHome ? m.winner === 'home' : m.winner === 'away')).length, 0);
            const myLosses = sports.reduce((s,[,matches]) =>
              s + matches.filter(m => m.winner && (isHome ? m.winner !== 'home' : m.winner !== 'away')).length, 0);

            return (
              <Card key={enc.encounter_id} className="bg-[#10131c] border-[#252a37] overflow-hidden">
                <button className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#161a26] transition-colors"
                  onClick={() => setOpenEncId(isOpen ? null : enc.encounter_id)}>
                  {/* Opponent logo */}
                  <div className="h-10 w-10 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-white text-sm overflow-hidden"
                    style={{ backgroundColor: oppColor || '#ef4444' }}>
                    {(oppName||'?')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#f0f2f7] truncate">vs {oppName || 'Opponent'}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      {enc.match_label && (
                        <span className="text-[10px] font-bold text-[#f5c518] bg-[rgba(245,197,24,0.1)] px-1.5 py-0.5 rounded">{enc.match_label}</span>
                      )}
                      <span className="text-[10px] text-[#8a93a3]">{enc.tournament_name}</span>
                      {enc.scheduled_at && (
                        <span className="text-[10px] text-[#8a93a3]">
                          {new Date(enc.scheduled_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',timeZone:'Asia/Kolkata'})}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-black tabular-nums">
                      <span className="text-[#22c55e]">{myWins}W</span>
                      <span className="text-[#8a93a3] mx-1">·</span>
                      <span className="text-[#ef4444]">{myLosses}L</span>
                    </p>
                    <p className="text-[10px] text-[#8a93a3]">{sports.length} sports</p>
                  </div>
                  {isOpen ? <ChevronUp className="h-4 w-4 text-[#8a93a3] flex-shrink-0"/> : <ChevronDown className="h-4 w-4 text-[#8a93a3] flex-shrink-0"/>}
                </button>

                {/* Drill-down: per-sport match results */}
                {isOpen && (
                  <div className="border-t border-[#252a37]">
                    {sports.map(([sc, matches]) => (
                      <div key={sc} className="border-b border-[#1e2435]/60 last:border-0">
                        <div className="flex items-center gap-2 px-4 py-2 bg-[#0f1219]">
                          <SportIcon sport={sc} size={14}/>
                          <span className="text-xs font-semibold text-[#8a93a3] capitalize">{sc.replace(/_/g,' ')}</span>
                          <span className="ml-auto text-xs font-bold text-[#f5c518]">
                            {matches.filter(m => isHome ? m.winner==='home' : m.winner==='away').length}–{matches.filter(m => m.winner && (isHome ? m.winner!=='home' : m.winner!=='away')).length}
                          </span>
                        </div>
                        {matches.map((m, mi) => {
                          const isResult = ['chess','carrom'].includes(sc);
                          const myWon  = isHome ? m.winner==='home' : m.winner==='away';
                          const oppWon = isHome ? m.winner==='away' : m.winner==='home';
                          const myPlayer  = isHome ? (m.home_player||m.home_team_name) : (m.away_player||m.away_team_name);
                          const oppPlayer = isHome ? (m.away_player||m.away_team_name) : (m.home_player||m.home_team_name);
                          const scoreStr  = isResult ? null
                            : (m.home_score != null ? `${isHome ? m.home_score : m.away_score}–${isHome ? m.away_score : m.home_score}` : null);
                          return (
                            <div key={mi} className="flex items-center gap-3 px-4 py-2 border-t border-[#1e2435]/40">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[rgba(59,130,246,0.15)] text-[#3b82f6] flex-shrink-0">
                                {m.slot || `M${mi+1}`}
                              </span>
                              <span className={`text-xs font-semibold flex-1 truncate ${myWon ? 'text-[#22c55e]' : 'text-[#f0f2f7]'}`}>
                                {myPlayer || myName}
                              </span>
                              <span className="text-xs font-black tabular-nums text-[#f5c518] flex-shrink-0">
                                {isResult
                                  ? (myWon ? 'Win' : oppWon ? 'Loss' : m.winner==='draw' ? 'Draw' : '–')
                                  : (scoreStr || '–')}
                              </span>
                              <span className={`text-xs flex-1 truncate text-right ${oppWon ? 'text-[#22c55e]' : 'text-[#8a93a3]'}`}>
                                {oppPlayer || oppName}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Transfer Player Dialog ─────────────────────────────── */}
      <Dialog open={!!transferTarget} onOpenChange={o => { if (!o) setTransferTarget(null); }}>
        <DialogContent className="bg-[#10131c] border-[#252a37] text-[#f0f2f7]">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">Transfer Player</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#8a93a3]">
              Move <span className="text-[#f5c518] font-semibold">{transferTarget?.player_name || 'this player'}</span> to another team.
              They will be removed from <span className="text-[#f5c518] font-semibold">{team?.name}</span>.
            </p>
            <div>
              <Label className="text-[#8a93a3] mb-1.5 block">Destination Team</Label>
              <select
                value={transferTeamId}
                onChange={e => setTransferTeamId(e.target.value)}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-2 text-sm focus:border-[#f5c518] outline-none">
                <option value="">Select a team…</option>
                {allTeams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferTarget(null)}
              className="border-[#252a37] text-[#8a93a3]">Cancel</Button>
            <Button onClick={confirmTransfer} disabled={transferring || !transferTeamId}
              className="bg-[#f5c518] text-[#0b0f18] font-bold hover:bg-[#f7d24a]">
              {transferring ? 'Transferring…' : 'Transfer Player'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Player Dialog ─────────────────────────────────── */}
      <Dialog open={addOpen} onOpenChange={o => { setAddOpen(o); if (!o) { setPlayerSearch(''); setSearchResults([]); setAddingSports([]); } }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md" data-testid="add-player-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#f5c518]"/>Add Player to {team?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]"/>
              <Input value={playerSearch} onChange={e => handlePlayerSearch(e.target.value)}
                placeholder="Search unassigned members by name or phone..."
                className="pl-10 bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"
                autoFocus data-testid="add-player-search"/>
            </div>

            {/* Results */}
            {searching && <p className="text-xs text-[#8a93a3] text-center py-2">Searching…</p>}
            {!searching && searchResults.length > 0 && (
              <div className="bg-[#1d2230] rounded-lg border border-[#252a37] overflow-hidden max-h-48 overflow-y-auto">
                {searchResults.map(p => {
                  const name = p.full_name || `${p.first_name} ${p.last_name||''}`.trim();
                  return (
                    <div key={p.id} onClick={() => handleAddPlayer(p)}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#252a37] border-b border-[#252a37]/50 last:border-0">
                      <div className="h-8 w-8 rounded-full bg-[#252a37] flex items-center justify-center text-[#f5c518] font-medium text-sm flex-shrink-0">
                        {name[0]?.toUpperCase() || 'P'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#f0f2f7] font-medium truncate">{name}</p>
                        {p.contact_phone && <p className="text-xs text-[#8a93a3] font-mono">{p.contact_phone}</p>}
                      </div>
                      <div className="flex flex-wrap gap-1 flex-shrink-0">
                        {(p.sports||[]).slice(0,2).map(sc => (
                          <SportIcon sport={sc} size={12}/>
                        ))}
                      </div>
                      <Plus className="h-4 w-4 text-[#f5c518] flex-shrink-0"/>
                    </div>
                  );
                })}
              </div>
            )}
            {!searching && playerSearch && searchResults.length === 0 && (
              <p className="text-xs text-[#8a93a3] text-center py-3">
                No unassigned members found for "{playerSearch}"
                <span className="block mt-1 text-[10px]">Only members not yet in any team are shown</span>
              </p>
            )}

            {/* Sports selection for this team */}
            {sports.length > 0 && (
              <div>
                <Label className="text-[#8a93a3] text-xs mb-2 block">Sports they play in this team</Label>
                <div className="flex flex-wrap gap-2">
                  {sports.map(sp => {
                    const sel = addingSports.includes(sp.code);
                    return (
                      <button key={sp.code} type="button" onClick={() => toggleAddSport(sp.code)}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                          sel ? 'bg-[rgba(245,197,24,0.12)] border-[rgba(245,197,24,0.4)] text-[#f5c518]'
                              : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[#8a93a3]'
                        }`}>
                        <><SportIcon sport={sp.code} size={14}/> {sp.name}</>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#8a93a3] mt-1">Select sports first, then click a player to add them.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm" data-testid="edit-team-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#f5c518]"/>Edit Team
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Logo + Color */}
            <div className="flex items-center gap-4">
              <div>
                <div
                  onClick={() => editFileRef.current?.click()}
                  className="h-14 w-14 rounded-xl cursor-pointer overflow-hidden flex items-center justify-center border-2 border-dashed border-[#252a37] hover:border-[#f5c518] transition-colors"
                  style={{ backgroundColor: editForm.color }}
                  title="Click to change logo"
                >
                  {editLogoPreview
                    ? <img src={editLogoPreview} className="h-full w-full object-cover" alt=""/>
                    : <span className="text-white font-bold text-lg">{editForm.short_name?.[0] || editForm.name?.[0] || 'T'}</span>
                  }
                </div>
                <input type="file" accept="image/*" ref={editFileRef} hidden onChange={handleEditLogo}/>
                <p className="text-[10px] text-[#8a93a3] text-center mt-1">Click to change</p>
              </div>
              <div className="flex-1 space-y-2">
                <div>
                  <Label className="text-[#8a93a3] text-xs">Team Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={editForm.color}
                      onChange={e => setEditForm({...editForm, color: e.target.value})}
                      className="h-8 w-8 rounded cursor-pointer border-0 bg-transparent p-0.5"/>
                    <span className="text-xs text-[#8a93a3] font-mono">{editForm.color}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[#8a93a3]">Team Name *</Label>
              <Input value={editForm.name}
                onChange={e => setEditForm({...editForm, name: e.target.value})}
                placeholder="e.g., Raptors FC"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="edit-team-name-input"/>
            </div>
            <div>
              <Label className="text-[#8a93a3]">Short Code (max 5)</Label>
              <Input value={editForm.short_name}
                onChange={e => setEditForm({...editForm, short_name: e.target.value.toUpperCase().slice(0,5)})}
                placeholder="e.g., RFC"
                maxLength={5}
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] uppercase mt-1"
                data-testid="edit-team-shortname-input"/>
            </div>
          </div>
          {/* Team Group Photo */}
          <div>
            <Label className="text-[#8a93a3]">Team Group Photo</Label>
            <div className="mt-1 space-y-2">
              {editPhotoPreview && (
                <img src={editPhotoPreview} alt="Team Photo"
                  className="w-full h-32 object-cover rounded-xl border border-[#252a37]"/>
              )}
              <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-xl border border-dashed border-[#252a37] text-[#8a93a3] hover:border-[rgba(245,197,24,0.4)] hover:text-[#f5c518] transition-colors text-xs">
                <Camera className="h-4 w-4"/>
                {editPhotoPreview ? 'Change Team Photo' : 'Upload Team Group Photo'}
                <input type="file" accept="image/*" className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => { setEditPhotoPreview(ev.target.result); setEditPhotoB64(ev.target.result); };
                    reader.readAsDataURL(file);
                  }}/>
              </label>
              {editPhotoPreview && editPhotoPreview !== team?.team_photo_url && (
                <button onClick={() => { setEditPhotoPreview(team?.team_photo_url || null); setEditPhotoB64(null); }}
                  className="text-[10px] text-[#ef4444] hover:underline">Revert</button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-[#252a37] text-[#f0f2f7]">
              Cancel
            </Button>
            <Button onClick={handleSaveTeam} disabled={saving} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="edit-team-save-button">
              {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2"/>Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
